<template>
	<client-only>
		<van-popup class="slide-sheet"
				   :class="{'slide-sheet--fixed-height': props.fixedHeight}"
				   v-model:show="isActive"
				   teleport=".dropdown-portal"
				   :position="displayPosition"
				   round
				   closeable
				   transition-appear
				   :style="{width: props.width ? `${props.width}px` : undefined}" @closed="$emit('closed')">

			<div v-if="props.title || props.description || $slots.header" class="slide-sheet__header">
				<span v-if="props.title" class="slide-sheet__title">{{ props.title }}</span>
				<span v-if="props.description" class="slide-sheet__description">{{ props.description }}</span>
				<slot name="header"/>
			</div>

			<div class="slide-sheet__content">
				<slot/>
			</div>

			<div v-if="$slots.footer"  class="slide-sheet__footer">
				<slot name="footer"/>
			</div>
		</van-popup>
	</client-only>
</template>

<script setup lang="ts">
import type {PopupPosition} from 'vant';
import {ToggleableComponentEmits, type ToggleableComponentProps} from "../../types";
import {computed} from "vue";
import type {Ref} from "vue";
import {useNuxtApp, useToggleableComponent} from "#imports";
import 'vant/es/popup/style';

const props = defineProps<{
	// A title to display above the content
	title?: string,

	// A title to display above the content
	description?: string,

	// The (maximum) width of the slide sheet. The displayed width will never exceed 100% of the screen width.
	width?: number,

	// Where the sheet will appear.
	// If unset, it will appear at the `desktopPosition` for desktop devices or when the width is set below a threshold, and at the bottom for mobile devices.
	position?: PopupPosition,

	// Where the sheet will appear on desktop devices.
	// If unset, it will appear at the center.
	desktopPosition?: PopupPosition,

	// Whether the sheet should have a fixed height of the max configured height.
	// This is required to correctly display specific scrollable components within the sheet, e.g., CalendarInput.
	fixedHeight?: boolean

} & ToggleableComponentProps>();

const emit = defineEmits([...ToggleableComponentEmits, 'closed']);
const {isActive} = useToggleableComponent(props, emit);
const {$device} = useNuxtApp();

const displayPosition: Ref<PopupPosition> = computed(() => {
	if ($device.isDesktop && props.desktopPosition) {
		return props.desktopPosition;
	}

	if (props.position) {
		return props.position;
	}

	if ($device.isMobile && (!props.width || props.width >= 420)) {
		return 'bottom';
	}

	return 'center';
});

</script>

<style>
/*********************
  Inputs
**********************/
.slide-sheet {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.slide-sheet.van-popup--bottom, .slide-sheet.van-popup--center, .slide-sheet.van-popup--top {
  max-height: var(--van-action-sheet-max-height);
}
.slide-sheet.van-popup--bottom.slide-sheet--fixed-height, .slide-sheet.van-popup--center.slide-sheet--fixed-height, .slide-sheet.van-popup--top.slide-sheet--fixed-height {
  height: var(--van-action-sheet-max-height);
}
.slide-sheet.van-popup--left, .slide-sheet.van-popup--right {
  width: var(--van-popup-side-width);
  height: 100%;
}
.slide-sheet.van-popup--bottom {
  max-width: 100%;
}
.slide-sheet.van-popup--bottom .slide-sheet__content {
  min-height: 160px;
}
.slide-sheet.van-popup--center.van-fade-enter-active {
  animation: slideSheetSlideInFromBottom 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards !important;
}
.slide-sheet.van-popup--center.van-fade-leave-active {
  animation: slideSheetSlideOutToBottom 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards !important;
}
.slide-sheet .slide-sheet__header {
  flex-shrink: 0;
  min-height: var(--van-action-sheet-header-height);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
}
.slide-sheet .slide-sheet__header .slide-sheet__title {
  font-weight: var(--van-font-bold);
  font-size: var(--van-action-sheet-header-font-size);
  line-height: var(--van-action-sheet-header-height);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  padding: 0 calc(var(--van-popup-close-icon-margin) + var(--van-popup-close-icon-size) + var(--van-padding-base));
  box-sizing: border-box;
}
.slide-sheet .slide-sheet__header .slide-sheet__description {
  font-size: calc(var(--van-action-sheet-header-font-size) - 3px);
  color: #5e6b6b;
  padding: 0 16px;
  margin-bottom: 4px;
}
.slide-sheet .slide-sheet__content {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@keyframes slideSheetSlideInFromBottom {
  0% {
    transform: translateY(1000px);
    opacity: 0;
  }
  100% {
    transform: translateY(-50%);
    opacity: 1;
  }
}
@keyframes slideSheetSlideOutToBottom {
  0% {
    transform: translateY(-50%);
    opacity: 1;
  }
  100% {
    transform: translateY(1000px);
    opacity: 0;
  }
}
</style>